/**
 * Legacy Launcher Persistence Adapter – Phase 3B.
 *
 * Owns the localStorage write path for legacy-sourced launcher layouts.
 * This is the existing behavior, now formalized as an adapter.
 *
 * Phase 5 addition: also syncs layout to Firestore via PUT /api/v2/users/me/prefs/layout
 * so that workspace import (Import Launcher) reads the latest data.
 * Firestore sync is debounced at 5s to batch rapid edits and save costs.
 */
import { writeLayoutToStorage } from '../launcherPersistenceService';
import { fetchWithAuth } from '../../../api/backendApi';
import { interpretLayoutSaveError, layoutSaveContext } from '../homeLayoutSync';

let _firestoreSyncTimer = null;
let _pending = null;
const FIRESTORE_SYNC_DEBOUNCE_MS = 5000;

const _layoutBody = (layoutSnapshot, tenantId) => {
  const body = { ...(layoutSnapshot || {}) };
  delete body.shortcutsOpen;
  delete body.shortcutsWidth;
  if (tenantId) body.tenantId = tenantId;
  if (body.baseRevision == null && body.revision != null) body.baseRevision = body.revision;
  delete body.revision;
  return body;
};

const _rejectSave = (error) => {
  const interpreted = interpretLayoutSaveError(error);
  const wrapped = new Error(interpreted.message);
  wrapped.code = interpreted.phase === 'conflict' ? 'layout_conflict' : 'layout_save_failed';
  wrapped.phase = interpreted.phase;
  wrapped.current = interpreted.current || null;
  return wrapped;
};

const _send = async (pending) => {
  try {
    const response = await fetchWithAuth('/api/v2/users/me/prefs/layout', 'PUT', pending.body, {
      suppressErrorLog: true,
    });
    const saved = response?.data || response;
    if (pending.uid) {
      writeLayoutToStorage(pending.uid, {
        ...pending.body,
        ...(saved && typeof saved === 'object' ? saved : {}),
        pendingSave: false,
      }, pending.body?.tenantId);
    }
    pending.waiters.splice(0).forEach((waiter) => waiter.resolve(saved));
    return saved;
  } catch (error) {
    const wrapped = _rejectSave(error);
    pending.waiters.splice(0).forEach((waiter) => waiter.reject(wrapped));
  }
};

const _flushPending = () => {
  const pending = _pending;
  _pending = null;
  clearTimeout(_firestoreSyncTimer);
  _firestoreSyncTimer = null;
  if (!pending) return Promise.resolve(null);
  return _send(pending);
};

/**
 * Send the save that was started for its original user and tenant.
 * A newer context must not reuse this body.
 */
export const flushPendingLayoutSave = () => _flushPending();

const _schedule = (uid, body, { immediate = false, contextKey }) => {
  if (_pending && _pending.contextKey !== contextKey) {
    _flushPending();
  }
  let pending = _pending && _pending.contextKey === contextKey ? _pending : null;
  if (!pending) {
    pending = { contextKey, uid, body, waiters: [] };
    _pending = pending;
  }
  pending.body = body;
  const promise = new Promise((resolve, reject) => {
    pending.waiters.push({ resolve, reject });
  });
  clearTimeout(_firestoreSyncTimer);
  if (immediate) {
    _pending = null;
    _send(pending);
    return promise;
  }
  _firestoreSyncTimer = setTimeout(() => {
    if (_pending !== pending) return;
    _pending = null;
    _send(pending);
  }, FIRESTORE_SYNC_DEBOUNCE_MS);
  return promise;
};

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    flushPendingLayoutSave();
  });
}

/**
 * Save a layout snapshot for a legacy-sourced runtime.
 * The returned promise resolves only after the server accepts the write.
 *
 * @param {string} uid           – current user uid
 * @param {object} layoutSnapshot – { icons, theme, gridColumns, … }
 */
export const save = (uid, layoutSnapshot, options = {}) => {
  if (!uid) return Promise.resolve(null);
  const stored = writeLayoutToStorage(uid, { ...layoutSnapshot, pendingSave: true }, options?.tenantId);
  if (stored === false) return Promise.reject(new Error('Could not save layout'));
  const body = _layoutBody(layoutSnapshot, options?.tenantId);
  const contextKey = layoutSaveContext({
    uid,
    tenantId: options?.tenantId || body.tenantId || '',
    workspaceId: layoutSnapshot?.seededFromWorkspaceId || '',
  });
  return _schedule(uid, body, {
    immediate: Boolean(options?.immediate),
    contextKey,
  });
};
